const {
    getIsAuthenticated,
    currentFirebaseTokenKey,
    backgroundFirebaseTokenKey,
    persistGet,
    persistRemove,
    persistSet,
    updateFirebaseToken,
    isCordovaRunning,
    onDeviceReady,
    onPause,
    onResume,
} = require('utils/cordova');
const { openAppDestination, navigateToPath } = require('services/auth-management/open-app-destination');
const ClientDebug = require('services/client-debug');
const Continuation = require('services/auth-management/continuation');
const PersistenceConfig = require('utils/persistence-config');
const { hasWelcomeEmailContext } = require('utils/signup-context');
const AppDiagnostics = require('utils/app-diagnostics');

const internals = {};
internals.lastHandledOpenUrlKey = PersistenceConfig.keys.cordova.lastHandledOpenUrl;
internals.lastHandledOpenUrlAtKey = PersistenceConfig.keys.cordova.lastHandledOpenUrlAt;
internals.openUrlDedupWindowMs = 30 * 1000;

module.exports = (store) => {

    // polyfill for scroll and scrollTo functions.  Needed for Android < 7.1
    // put it before web browsers getting kicked out of here in case old browsers want it too
    if (!Element.prototype.scroll) {
        Element.prototype.scroll = Element.prototype.scrollTo = function (x, y) {

            this.scrollTop = y;
        };
    }

    if (!isCordovaRunning()) {
        // Bail
        return;
    }

    let isBound = false;
    let previousIsAuthenticated = false;
    let openUrlVersion = 0;
    let pendingAppPath = null;

    // Handles cases where resuming the app should not reload and rerender the application
    // So far, the only case this handles is selecting an image from Android's built-in image gallery
    // on the edit profile and profile details pages. Opening that image dialog pauses the application.
    // Selecting an image then causes our onResume handler to rerender the application,
    // losing our selected image and any other non-persisted component state.
    // (on iOS, the native image selection dialog does not pause the application)

    window.__nearpeerAppLinkHandler = function (url) {

        AppDiagnostics.record('handler.received', { destination: AppDiagnostics.safeLocation(url) });
        const incomingPath = internals.getPathFromOpenUrl(url);

        if (!incomingPath) {
            AppDiagnostics.record('handler.rejected', { reason: 'invalid-or-disallowed-url' });
            return;
        }

        const debugSession = ClientDebug.activateFromUrl(url);
        const path = debugSession ? '/login' : incomingPath;

        if (pendingAppPath === path) {
            AppDiagnostics.record('handler.coalesced');
            return;
        }

        const currentPath = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
        const persistedLastHandledOpenUrl = persistGet(internals.lastHandledOpenUrlKey);
        const persistedLastHandledOpenUrlAt = Number(persistGet(internals.lastHandledOpenUrlAtKey) || 0);

        if (!internals.shouldHandleOpenUrl({
            currentPath,
            nextPath: path,
            lastHandledOpenUrl: internals.lastHandledOpenUrl,
            persistedLastHandledOpenUrl,
            persistedLastHandledOpenUrlAt
        })) {
            AppDiagnostics.record('handler.skipped', { reason: 'current-route-or-duplicate' });
            return;
        }

        const location = new URL(url);

        if (!Continuation.captureFromLocation(location)) {
            Continuation.captureDestination(location);
        }

        internals.lastHandledOpenUrl = path;
        persistSet(internals.lastHandledOpenUrlKey, path);
        persistSet(internals.lastHandledOpenUrlAtKey, String(Date.now()));

        const version = ++openUrlVersion;

        if (path.startsWith('/app/') || hasWelcomeEmailContext(location)) {
            pendingAppPath = path;

            const openPath = path.startsWith('/app/') ? internals.openAppPath : internals.openInvitePath;

            return openPath({
                path,
                store,
                isCurrent: () => version === openUrlVersion
            }).catch((error) => {

                AppDiagnostics.record('handler.failed', { name: error?.name, code: error?.code });
                console.error('[app-link] failed to open destination', error);
            }).finally(() => {

                if (version === openUrlVersion) {
                    pendingAppPath = null;
                }

                AppDiagnostics.record('handler.settled', { current: version === openUrlVersion });
            });
        }

        pendingAppPath = null;
        internals.navigateToPath({
            path,
            store
        });
    };

    const pendingLinks = window.__nearpeerPendingAppLinks || [];
    AppDiagnostics.record('handler.ready', { queuedLinks: pendingLinks.length });
    window.__nearpeerPendingAppLinks = [];
    pendingLinks.forEach(window.__nearpeerAppLinkHandler);

    document.addEventListener('deviceready', (args) => {

        store.subscribe(() => {

            const isAuthenticated = getIsAuthenticated();

            if (!isBound && isAuthenticated) {
                isBound = true;

                const bgFirebaseKey = persistGet(backgroundFirebaseTokenKey);

                if (bgFirebaseKey) {
                    updateFirebaseToken(bgFirebaseKey);
                }
            }

            if (previousIsAuthenticated && !isAuthenticated) {
                const registrationId = persistGet(currentFirebaseTokenKey);
                persistRemove(currentFirebaseTokenKey);
                persistSet(backgroundFirebaseTokenKey, registrationId);
                isBound = false;
            }

            previousIsAuthenticated = isAuthenticated;
        });

        onDeviceReady(args);
    });

    document.addEventListener('pause', onPause);
    document.addEventListener('resume', onResume);
};

internals.lastHandledOpenUrl = null;

internals.openInvitePath = async ({ path, store, context = require('app-context').context, isCurrent = () => true }) => {

    AppDiagnostics.record('auth.wait', { kind: 'invite' });
    // A pending saved-token login can still redirect to the app loader. Let it
    // settle before replacing that session with the invitation's identity.
    await context.handlers.auth.resolveIsAuthenticated();
    AppDiagnostics.record('auth.ready', { kind: 'invite' });

    if (!isCurrent()) {
        AppDiagnostics.record('handler.superseded');
        return;
    }

    await store.dispatch(context.actions.auth.logoutSync({ redirect: false }));
    AppDiagnostics.record('invite.session-cleared');

    if (isCurrent()) {
        internals.navigateToPath({ path, store });
    }
};

internals.openAppPath = openAppDestination;
internals.navigateToPath = navigateToPath;

internals.getPathFromOpenUrl = (url) => {

    if (typeof url !== 'string' || !url) {
        return '';
    }

    try {
        const parsedUrl = new URL(url);

        if (parsedUrl.protocol !== 'https:' || parsedUrl.username || parsedUrl.password || parsedUrl.port ||
            !['nearpeer.net', 'staging.nearpeer.net', 'teams.nearpeer.net'].includes(parsedUrl.hostname)) {
            return '';
        }

        return `${parsedUrl.pathname || ''}${parsedUrl.search || ''}${parsedUrl.hash || ''}` || '';
    }
    catch (err) {
        return '';
    }
};

internals.shouldHandleOpenUrl = ({
    currentPath,
    nextPath,
    lastHandledOpenUrl,
    persistedLastHandledOpenUrl,
    persistedLastHandledOpenUrlAt
}) => {

    if (!nextPath) {
        return false;
    }

    // Navigation links can be intentionally tapped again from another screen.
    // Only authentication callbacks need persistent replay suppression.
    if (nextPath.startsWith('/app/') || hasWelcomeEmailContext(new URL(nextPath, 'https://nearpeer.net'))) {
        return nextPath !== currentPath;
    }

    const wasPersistedDuplicateRecently = persistedLastHandledOpenUrl === nextPath
        && persistedLastHandledOpenUrlAt
        && (Date.now() - persistedLastHandledOpenUrlAt) < internals.openUrlDedupWindowMs;

    const isUnpersistedDuplicate = !persistedLastHandledOpenUrlAt && nextPath === lastHandledOpenUrl;

    return nextPath !== currentPath && !isUnpersistedDuplicate && !wasPersistedDuplicateRecently;
};

module.exports.internals = internals;
